import { Avatar } from "antd";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getCompanyProfile } from "redux/selectors";

const RED = "#e63946";
const DARK = "#2b2d42";
const LIGHT_GRAY = "#f5f5f5";

const sectionTitle = {
  color: RED,
  fontSize: 10,
  fontWeight: "bold",
  marginBottom: 6,
  marginTop: 0,
  fontFamily: "Arial, sans-serif",
};

const bodyText = {
  fontSize: 10,
  color: "#333",
  margin: "2px 0",
  fontFamily: "Arial, sans-serif",
  lineHeight: "1.5",
};

const CvDownload = forwardRef(({ cvUser, clientData, onImageReady, matchedFinanceUser }, ref) => {
  const companyProfile = useSelector(getCompanyProfile);
  const companyName = companyProfile?.company_name || "BDO East Africa";
  const profileUrl = cvUser?.profileurl || null;
  const blobUrlRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (!cvUser?.profileurl) {
      setImgSrc(null);
      onImageReady?.();
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        const base64 = canvas.toDataURL("image/jpeg");
        setImgSrc(base64);
      } catch (e) {
        console.log("Canvas failed, fallback", e);
        setImgSrc(img.src);
      }

      onImageReady?.();
    };

    img.onerror = () => {
      setImgSrc(null);
      onImageReady?.();
    };

    img.src = cvUser.profileurl + "?t=" + Date.now();
  }, [cvUser?.profileurl]);

  if (!cvUser) return null;

  const certifications = cvUser.professionalcertifications || [];

  const expertise = Array.isArray(cvUser.expertise)
    ? cvUser.expertise
    : cvUser.expertise
      ? [cvUser.expertise]
      : [];

  const languages = Array.isArray(cvUser.languages)
    ? cvUser.languages
    : cvUser.languages
      ? [cvUser.languages]
      : [];

  const buildClientRows = () => {
    if (!Array.isArray(clientData) || clientData.length === 0) return [];

    const map = {};

    clientData.forEach((eng) => {
      const company = eng.clientName || "-";
      const service = eng.serviceline || eng.competency || "-";

      if (!map[company]) {
        map[company] = {
          company,
          servicelines: new Set(),
          roles: new Set(),
        };
      }

      map[company].servicelines.add(service);

      if (Array.isArray(eng.employeeList) && eng.employeeList.length > 0) {
        eng.employeeList.forEach((emp) => {
          map[company].roles.add(emp.projectRole || "-");
        });
      } else {
        map[company].roles.add("-");
      }
    });

    return Object.values(map).map((item) => ({
      company: item.company,
      serviceline: Array.from(item.servicelines).join(", "),
      role: Array.from(item.roles).join(", "),
    }));
  };

  const clientRows = buildClientRows();

  return (
    <div
      ref={ref}
      style={{
        width: "794px",
        minHeight: "1123px",         /* A4 at 96dpi — prevents content shrink */
        fontFamily: "Arial, sans-serif",
        background: "#ffffff",
        boxSizing: "border-box",
        overflow: "visible",         /* let content grow naturally */
        paddingBottom: "20px",
      }}
    >
      {/* ── TOP RED BANNER ── */}
      <div style={{ background: RED, padding: "10px 24px" }}>
        <p style={{ color: "#fff", fontSize: 10, fontWeight: "bold", margin: 0, letterSpacing: 1 }}>
          RESUMES OF PROPOSED TEAM
        </p>
      </div>

      {/* ── NAME ROW ── */}
      <div style={{ padding: "10px 24px 8px", borderBottom: "1px solid #ddd" }}>
        <p style={{ fontWeight: "bold", fontSize: 10, color: DARK, margin: 0, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {cvUser.fullname?.toUpperCase()} – {companyName.toUpperCase()}
        </p>
      </div>

      {/* ── BODY ── */}
      <div style={{ display: "flex" }}>

        {/* ══ LEFT COLUMN ══ */}
        <div
          style={{
            width: "222px",
            flexShrink: 0,
            background: LIGHT_GRAY,
            padding: "20px 16px",
            boxSizing: "border-box",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          {/* Profile Photo */}
          <div style={{ marginBottom: 18, textAlign: "center" }}>
            {imgSrc ? (
              <img
                src={imgSrc}
                style={{
                  width: "120px",
                  height: "140px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div>No Photo</div>
            )}
          </div>

          {/* Contact Information */}
          <div style={{ marginBottom: 14 }}>
            <p style={sectionTitle}>Contact Information</p>
            <p style={bodyText}>Direct: {cvUser.phoneno || "-"}</p>
            <p style={bodyText}>Tel: {matchedFinanceUser || "-"}</p>
            <p style={{ ...bodyText, wordBreak: "break-all" }}>{cvUser.userid || "-"}</p>
          </div>

          {/* Areas of Expertise */}
          <div style={{ marginBottom: 14 }}>
            <p style={sectionTitle}>Areas of Expertise</p>
            <ul style={{ paddingLeft: 14, margin: 0 }}>
              {expertise.length > 0 ? (
                expertise.map((item, i) => (
                  <li key={i} style={{ ...bodyText, marginBottom: 2 }}>{item}</li>
                ))
              ) : (
                <li style={bodyText}>-</li>
              )}
            </ul>
          </div>

          {/* Languages (written & spoken) */}
          <div style={{ marginBottom: 14 }}>
            <p style={sectionTitle}>Languages (written &amp; spoken)</p>
            <ul style={{ paddingLeft: 14, margin: 0 }}>
              {languages.length > 0 ? (
                languages.map((lang, i) => (
                  <li key={i} style={{ ...bodyText, marginBottom: 2 }}>{lang}</li>
                ))
              ) : (
                <li style={bodyText}>-</li>
              )}
            </ul>
          </div>

          {/* Qualifications & Certifications */}
          <div style={{ marginBottom: 14 }}>
            <p style={sectionTitle}>Qualifications &amp; Certifications</p>
            <ul style={{ paddingLeft: 14, margin: 0 }}>
              {certifications.length > 0 ? (
                certifications.map((cert, i) => (
                  <li key={i} style={{ ...bodyText, marginBottom: 2 }}>
                    {cert.title || cert.certificationname || "-"}
                    {cert.certificationtype ? ` (${cert.certificationtype})` : ""}
                  </li>
                ))
              ) : (
                <li style={bodyText}>-</li>
              )}
            </ul>
          </div>
        </div>

        {/* ══ RIGHT COLUMN ══ */}
        <div style={{ flex: 1, padding: "20px", boxSizing: "border-box", minWidth: 0 }}>

          {/* Proposed Responsibilities */}
          <div style={{ marginBottom: 16 }}>
            <p style={sectionTitle}>Proposed Responsibilities:</p>
            <p style={bodyText}>XXXXXXX</p>
          </div>

          {/* Background and Relevant Experience */}
          <div style={{ marginBottom: 16 }}>
            <p style={sectionTitle}>Background and Relevant Experience</p>
            <p style={{ ...bodyText, lineHeight: "1.6" }}>
              {cvUser.backgroundExperience || "-"}
            </p>
          </div>

          {/* Client experience table */}
          <div style={{ marginBottom: 16 }}>
            <p style={sectionTitle}>Client experience includes:</p>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 10,
                tableLayout: "fixed",   /* fixed layout prevents columns from overflowing */
              }}
            >
              <colgroup>
                <col style={{ width: "35%" }} />
                <col style={{ width: "40%" }} />
                <col style={{ width: "25%" }} />
              </colgroup>
              <thead>
                <tr style={{ background: LIGHT_GRAY }}>
                  {["Company", "Serviceline", "Role on the Job"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "6px 8px",
                        border: "1px solid #ddd",
                        textAlign: "left",
                        fontWeight: "bold",
                        color: DARK,
                        fontSize: 10,
                        whiteSpace: "normal",   /* allow wrap in header */
                        wordBreak: "break-word",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clientRows.length > 0 ? (
                  clientRows.map((row, i) => (
                    <tr
                      key={i}
                      style={{
                        background: i % 2 === 0 ? "#fff" : "#fafafa",
                        pageBreakInside: "avoid",
                        breakInside: "avoid",
                      }}
                    >
                      <td
                        style={{
                          padding: "6px 8px",
                          border: "1px solid #ddd",
                          color: "#333",
                          whiteSpace: "normal",     /* allow wrapping */
                          wordBreak: "break-word",  /* break long words, not mid-word arbitrarily */
                          overflowWrap: "break-word",
                          verticalAlign: "top",
                        }}
                      >
                        {row.company}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          border: "1px solid #ddd",
                          color: "#333",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          verticalAlign: "top",
                        }}
                      >
                        {row.serviceline}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          border: "1px solid #ddd",
                          color: "#333",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          verticalAlign: "top",
                        }}
                      >
                        {row.role}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: "6px 8px",
                        border: "1px solid #ddd",
                        color: "#999",
                        textAlign: "center",
                      }}
                    >
                      No client experience data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
});

export default CvDownload;